import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { loginRedirectUri } from "@/lib/keycloak";

interface ProtectedRouteProps {
  /**
   * At least one of these realm roles is required. Omit to allow any
   * authenticated user (no role check).
   */
  requiredRoles?: readonly string[];
  children: ReactNode;
  /** Element rendered while Keycloak init is in flight. */
  loadingFallback?: ReactNode;
  /** Element rendered if user lacks the required role. */
  forbiddenFallback?: ReactNode;
  /** Optional local sign-in route instead of an immediate Keycloak redirect. */
  unauthenticatedRedirect?: string;
}

/**
 * Gates a route tree on authentication + optional role membership.
 *
 * Unauthenticated users normally go straight to Keycloak. Routes that need a
 * local membership/sign-in explanation can opt into `unauthenticatedRedirect`.
 *
 * Roles come from the `realm_access.roles` JWT claim. No DB round-trip.
 */
export function ProtectedRoute({
  requiredRoles,
  children,
  loadingFallback,
  forbiddenFallback,
  unauthenticatedRedirect,
}: ProtectedRouteProps) {
  const { isLoading, isAuthenticated, hasAnyRole, login } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated && !unauthenticatedRedirect) {
      void login({ redirectUri: loginRedirectUri() });
    }
  }, [isLoading, isAuthenticated, login, unauthenticatedRedirect]);

  if (isLoading) {
    return (
      <>
        {loadingFallback ?? (
          <div className="p-8 text-muted-foreground">Signing you in…</div>
        )}
      </>
    );
  }

  if (!isAuthenticated && unauthenticatedRedirect) {
    return (
      <Navigate
        to={unauthenticatedRedirect}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        {loadingFallback ?? (
          <div className="p-8 text-muted-foreground">Signing you in…</div>
        )}
      </>
    );
  }

  if (requiredRoles && requiredRoles.length > 0 && !hasAnyRole(...requiredRoles)) {
    return (
      <>
        {forbiddenFallback ?? (
          <div className="mx-auto max-w-xl p-8 space-y-3">
            <h1 className="text-2xl font-semibold">Access denied</h1>
            <p className="text-muted-foreground">
              This page requires the{" "}
              <code className="rounded bg-muted px-1">
                {requiredRoles.join(" / ")}
              </code>{" "}
              role. If that's wrong, contact your PulseBoard administrator.
            </p>
          </div>
        )}
      </>
    );
  }

  return <>{children}</>;
}
